import * as IoC from 'typescript-ioc';
import { IJobsMonitor, JobsSubscription } from '../jobs-monitor';
import { IJobDescriptionDTO, IJobsDAO, JobDetailsTypes, JobResultTypes, JobType } from '../../../api/jobs.dao';
import { JobDtoType } from '../../jobs';

type jobHandler<T extends JobDetailsTypes, K extends JobResultTypes | null = null> = (jobDescription: IJobDescriptionDTO<T, K>) => void;

class JobsMonitor extends IJobsMonitor {
    private intervalHandle: number;
    // works with any kind of jobs and multiply types are stored in single object
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    private registeredHandlers: { [jobsType: string]: jobHandler<any, any>[] };

    private activeJobsCache: IJobDescriptionDTO<JobDetailsTypes, JobResultTypes | null>[];
    // eslint-disable-next-line
    private readonly subscriptionRegistry: Set<JobsSubscription>;

    constructor(@IoC.Inject private readonly jobsDao: IJobsDAO) {
        super();
        this.registeredHandlers = {};
        this.activeJobsCache = null;
        // eslint-disable-next-line
        this.subscriptionRegistry = new Set<JobsSubscription>();
    }

    public initialize() {
        this.pollCheckJobStatuses();
    }

    public cleanup() {
        window.clearInterval(this.intervalHandle);
        this.registeredHandlers = {};
        this.subscriptions.clear();
    }

    public get subscriptions() {
        return this.subscriptionRegistry;
    }

    public subscribe = (subscription: JobsSubscription) => {
        this.subscriptions.add(subscription);
    };

    public registerHandler<T extends JobDetailsTypes, K extends JobResultTypes | null = null>(
        type: JobType,
        handler: (jobDescription: IJobDescriptionDTO<T, K>) => void
    ) {
        if (!this.registeredHandlers[type]) {
            this.registeredHandlers[type] = [];
        }
        this.registeredHandlers[type].push(handler);
    }

    public removeHandler<T extends JobDetailsTypes, K extends JobResultTypes | null = null>(
        type: JobType,
        handler: (jobDescription: IJobDescriptionDTO<T, K>) => void
    ) {
        if (!this.registeredHandlers[type]) {
            return;
        }
        const currentHandlerIndex = this.registeredHandlers[type].findIndex(registeredHandler => registeredHandler === handler);
        if (currentHandlerIndex !== -1) {
            this.registeredHandlers[type].splice(currentHandlerIndex, 1);
        }
    }

    public getJobsByTypeFromCache<T extends JobDetailsTypes, K extends JobResultTypes | null = null>(
        type: JobType
    ): IJobDescriptionDTO<T, K>[] {
        return this.activeJobsCache.filter(job => job.jobType === type) as IJobDescriptionDTO<T, K>[];
    }

    public cacheExists(): boolean {
        return this.activeJobsCache !== null;
    }

    private callSubscribers(jobs: JobDtoType[]): void {
        if (this.subscriptions.size) this.subscriptions.forEach(subscription => subscription(jobs));
    }

    private pollCheckJobStatuses = async () => {
        await this.checkJobStatuses();
        // make sure next poll request is made only after previous one is completed,
        // we don't want requests to overlap in order to avoid duplicate confirmation messages
        this.intervalHandle = window.setTimeout(this.pollCheckJobStatuses, 10000);
    };

    private checkJobStatuses = async () => {
        // while user is presented with the
        const activeJobs = await this.jobsDao.getAllJobsTriggeredByCurrentUser();

        this.callSubscribers(activeJobs as JobDtoType[]);

        this.activeJobsCache = activeJobs;

        for (const job of activeJobs) {
            const registeredHandlers = this.registeredHandlers[job.jobType];

            // if no handlers are registered for this job type - ignore it
            if (!registeredHandlers) {
                continue;
            }

            registeredHandlers.forEach(x => x(job));
        }
    };
}

export { JobsMonitor };
